/**
 * CanvasContextMenu.tsx - Right-click context menu for automation canvas
 *
 * Provides context-sensitive actions when right-clicking nodes:
 * - Switch nodes: Reorder segments (opens modal), duplicate, delete
 * - Regular steps: Duplicate, delete
 * - Segment nodes: Delete (except default)
 *
 * Behavior-case nodes do not expose a context menu. They are intentionally
 * locked and cannot be right-clicked or deleted directly.
 *
 * Related:
 *   - hooks/useContextMenu.ts - State management
 *   - components/AutomationFlowCanvas.tsx - Integration point
 *   - components/ReorderSegmentsModal.tsx - Segment reorder modal
 */

import { css } from 'aphrodite';
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ContextMenuState } from '../hooks/useContextMenu';
import { styleSheet } from './styles/CanvasContextMenuStyles';

/**
 * Icons for menu items
 */
const iconProps = {
	width: 16,
	height: 16,
	viewBox: '0 0 16 16',
	fill: 'none',
	stroke: 'currentColor',
	strokeWidth: 1.5,
	strokeLinecap: 'round' as const,
	strokeLinejoin: 'round' as const,
};

function TrashIcon() {
	return (
		<svg {...iconProps}>
			<path d='M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10' />
		</svg>
	);
}

function DuplicateIcon() {
	return (
		<svg {...iconProps}>
			<path d='M4 6h8v8H4zM6 4h8v8' />
		</svg>
	);
}

function ReorderIcon() {
	return (
		<svg {...iconProps}>
			<path d='M3 4h10M3 8h10M3 12h10' />
			<path d='M14 3l2 2-2 2M14 11l2 2-2 2' />
		</svg>
	);
}

function MoveUpIcon() {
	return (
		<svg {...iconProps}>
			<path d='M8 3v10M4 7l4-4 4 4' />
		</svg>
	);
}

function MoveDownIcon() {
	return (
		<svg {...iconProps}>
			<path d='M8 3v10M4 9l4 4 4-4' />
		</svg>
	);
}

interface CanvasContextMenuProps {
	/** Current context menu state */
	state: ContextMenuState;
	/** Callback to close the menu */
	onClose: () => void;
	/** Callback when delete is selected */
	onDelete: (nodeId: string) => void;
	/** Callback when duplicate is selected */
	onDuplicate: (nodeId: string) => void;
	/** Callback to open the reorder segments modal for a switch node */
	onOpenReorderSegments?: (switchNodeId: string) => void;
	/** Whether the switch node has enough segments to reorder (2+ non-default) */
	canReorderSegments?: boolean;
	/** Callback when move up is selected */
	onMoveUp?: (nodeId: string) => void;
	/** Callback when move down is selected */
	onMoveDown?: (nodeId: string) => void;
	/** Whether the step can move up */
	canMoveUp?: boolean;
	/** Whether the step can move down */
	canMoveDown?: boolean;
}

/**
 * Context menu component that renders at the click position.
 * Uses portal to render at document body level for proper z-index.
 */
export function CanvasContextMenu({
	state,
	onClose,
	onDelete,
	onDuplicate,
	onOpenReorderSegments,
	canReorderSegments = false,
	onMoveUp,
	onMoveDown,
	canMoveUp = false,
	canMoveDown = false,
}: CanvasContextMenuProps) {
	const menuRef = useRef<HTMLDivElement>(null);

	// Close on escape key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	// Close when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				onClose();
			}
		};

		// Delay adding listener to prevent immediate close from the right-click
		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	const handleDelete = useCallback(() => {
		if (state.targetNodeId) {
			onDelete(state.targetNodeId);
		}
		onClose();
	}, [state.targetNodeId, onDelete, onClose]);

	const handleDuplicate = useCallback(() => {
		if (state.targetNodeId) {
			onDuplicate(state.targetNodeId);
		}
		onClose();
	}, [state.targetNodeId, onDuplicate, onClose]);

	const handleReorderSegments = useCallback(() => {
		// For segments, use parent switch node ID; for switch nodes, use the node itself
		const switchNodeId = state.isSegmentNode ? state.parentSwitchNodeId : state.targetNodeId;
		if (switchNodeId && onOpenReorderSegments) {
			onOpenReorderSegments(switchNodeId);
		}
		onClose();
	}, [state.targetNodeId, state.isSegmentNode, state.parentSwitchNodeId, onOpenReorderSegments, onClose]);

	const handleMoveUp = useCallback(() => {
		if (state.targetNodeId && onMoveUp) {
			onMoveUp(state.targetNodeId);
		}
		onClose();
	}, [state.targetNodeId, onMoveUp, onClose]);

	const handleMoveDown = useCallback(() => {
		if (state.targetNodeId && onMoveDown) {
			onMoveDown(state.targetNodeId);
		}
		onClose();
	}, [state.targetNodeId, onMoveDown, onClose]);

	if (!state.isOpen) {
		return null;
	}

	// Calculate position, ensuring menu stays within viewport
	const menuStyle: React.CSSProperties = {
		left: state.position.x,
		top: state.position.y,
	};

	const menuContent = (
		<div ref={menuRef} className={css(styleSheet.menu)} style={menuStyle} role='menu' aria-label='Node actions'>
			{/* Reorder segments action for switch nodes OR segment nodes with 2+ segments */}
			{(state.isSwitchNode || (state.isSegmentNode && state.parentSwitchNodeId)) &&
				canReorderSegments &&
				onOpenReorderSegments && (
					<button type='button' className={css(styleSheet.menuItem)} onClick={handleReorderSegments} role='menuitem'>
						<ReorderIcon />
						<span>Reorder Segments</span>
					</button>
				)}

			{/* Move Up action - for step nodes and segments that can move up */}
			{!state.isTriggerNode && canMoveUp && onMoveUp && (
				<button type='button' className={css(styleSheet.menuItem)} onClick={handleMoveUp} role='menuitem'>
					<MoveUpIcon />
					<span>Move Up</span>
				</button>
			)}

			{/* Move Down action - for step nodes and segments that can move down */}
			{!state.isTriggerNode && canMoveDown && onMoveDown && (
				<button type='button' className={css(styleSheet.menuItem)} onClick={handleMoveDown} role='menuitem'>
					<MoveDownIcon />
					<span>Move Down</span>
				</button>
			)}

			{/* Duplicate action - hidden for triggers and segments */}
			{!state.isTriggerNode && !state.isSegmentNode && (
				<button type='button' className={css(styleSheet.menuItem)} onClick={handleDuplicate} role='menuitem'>
					<DuplicateIcon />
					<span>Duplicate</span>
				</button>
			)}

			{/* Delete action - hidden for default branches and triggers */}
			{!state.isDefaultSegment && !state.isTriggerNode && (
				<button
					type='button'
					className={css(styleSheet.menuItem, styleSheet.menuItemDanger)}
					onClick={handleDelete}
					role='menuitem'
				>
					<TrashIcon />
					<span>Delete</span>
				</button>
			)}

			{/* Message for default branch - cannot be deleted */}
			{state.isDefaultSegment && (
				<div className={css(styleSheet.protectedMessage)}>
					<span>Default branch cannot be deleted</span>
				</div>
			)}
		</div>
	);

	// Render via portal to ensure proper stacking
	return createPortal(menuContent, document.body);
}
